/**
 * Inline Approval Actions Component
 *
 * Compact approve/reject buttons for use in list views.
 * Opens modal dialogs for confirmation and comment entry.
 */

import { renderIcon, type IconName } from '../utils/icons';
import { Modal, type ModalAction } from './modal';
import { createTextarea } from './textarea';
import { apiClient } from '../services/api-client';

/**
 * Request data needed for actions
 */
export interface InlineApprovalRequest {
  id: string;
  title: string | null;
  amount: number | null;
  currency: string | null;
}

/**
 * Options for inline approval actions
 */
export interface InlineApprovalActionsOptions {
  /** The request to act on */
  request: InlineApprovalRequest;
  /** Callback when action completes successfully */
  onActionComplete?: (action: 'approve' | 'reject') => void;
  /** Callback when action fails */
  onActionError?: (action: 'approve' | 'reject', error: Error) => void;
}

/**
 * API response type
 */
interface ApprovalActionResponse {
  data: {
    request: unknown;
    approvalStep?: unknown;
    allSteps?: unknown[];
    workflowComplete?: boolean;
  };
  message: string;
}

/**
 * Create inline approval action buttons
 */
export function createInlineApprovalActions(options: InlineApprovalActionsOptions): HTMLDivElement {
  const { request, onActionComplete, onActionError } = options;

  const container = document.createElement('div');
  container.className = 'inline-approval-actions flex items-center gap-1 touch-spacing';

  /**
   * Set loading state
   */
  function setLoading(loading: boolean): void {
    const buttons = container.querySelectorAll('button');
    buttons.forEach((btn) => {
      btn.disabled = loading;
      if (loading) {
        btn.classList.add('opacity-50', 'cursor-not-allowed');
      } else {
        btn.classList.remove('opacity-50', 'cursor-not-allowed');
      }
    });
  }

  /**
   * Handle approve action
   */
  async function handleApprove(e: Event): Promise<void> {
    e.stopPropagation(); // Prevent row click

    const contentDiv = document.createElement('div');
    contentDiv.className = 'space-y-4';

    const message = document.createElement('p');
    message.className = 'text-muted-foreground';
    message.textContent = `Are you sure you want to approve "${request.title || 'Untitled'}"?`;
    contentDiv.appendChild(message);

    if (request.amount !== null && request.currency) {
      const amountInfo = document.createElement('p');
      amountInfo.className = 'text-sm text-muted-foreground';
      amountInfo.innerHTML = `<strong>Amount:</strong> ${request.currency} ${request.amount.toLocaleString()}`;
      contentDiv.appendChild(amountInfo);
    }

    // Comment textarea
    const commentLabel = document.createElement('label');
    commentLabel.className = 'block text-sm font-medium text-foreground';
    commentLabel.textContent = 'Comment (optional)';
    contentDiv.appendChild(commentLabel);

    const commentTextarea = createTextarea({
      placeholder: 'Add an optional comment...',
      rows: 3,
    });
    contentDiv.appendChild(commentTextarea);

    const actions: ModalAction[] = [
      {
        label: 'Cancel',
        variant: 'ghost',
      },
      {
        label: 'Approve',
        variant: 'primary',
        icon: 'check',
        primary: true,
        onClick: async () => {
          const comment = (commentTextarea as HTMLTextAreaElement).value.trim();
          setLoading(true);

          try {
            const response = await apiClient.post<ApprovalActionResponse>(
              `/requests/${request.id}/approve`,
              comment ? { comment } : {}
            );

            showApprovalToast(response.data.message || 'Request approved successfully', 'success');
            onActionComplete?.('approve');
            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to approve request';
            showApprovalToast(errorMessage, 'error');
            onActionError?.('approve', error instanceof Error ? error : new Error(errorMessage));
            return false;
          } finally {
            setLoading(false);
          }
        },
      },
    ];

    const modal = new Modal({
      title: 'Approve Request',
      content: contentDiv,
      variant: 'confirm',
      size: 'md',
      icon: 'checkCircle',
      iconClass: 'text-success',
      actions,
    });

    modal.open();
  }

  /**
   * Handle reject action
   */
  async function handleReject(e: Event): Promise<void> {
    e.stopPropagation(); // Prevent row click

    const contentDiv = document.createElement('div');
    contentDiv.className = 'space-y-4';

    const message = document.createElement('p');
    message.className = 'text-muted-foreground';
    message.textContent = `Are you sure you want to reject "${request.title || 'Untitled'}"?`;
    contentDiv.appendChild(message);

    const warningDiv = document.createElement('div');
    warningDiv.className = 'flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive';
    warningDiv.appendChild(renderIcon('alertTriangle', { size: 'sm', decorative: true }));
    const warningText = document.createElement('span');
    warningText.className = 'text-sm';
    warningText.textContent = 'This action cannot be undone. The request will be permanently rejected.';
    warningDiv.appendChild(warningText);
    contentDiv.appendChild(warningDiv);

    // Reason textarea
    const reasonLabel = document.createElement('label');
    reasonLabel.className = 'block text-sm font-medium text-foreground';
    reasonLabel.textContent = 'Reason for rejection (recommended)';
    contentDiv.appendChild(reasonLabel);

    const reasonTextarea = createTextarea({
      placeholder: 'Please provide a reason for rejecting this request...',
      rows: 3,
    });
    contentDiv.appendChild(reasonTextarea);

    const actions: ModalAction[] = [
      {
        label: 'Cancel',
        variant: 'ghost',
      },
      {
        label: 'Reject Request',
        variant: 'destructive',
        icon: 'x',
        primary: true,
        onClick: async () => {
          const reason = (reasonTextarea as HTMLTextAreaElement).value.trim();
          setLoading(true);

          try {
            const response = await apiClient.post<ApprovalActionResponse>(
              `/requests/${request.id}/reject`,
              reason ? { comment: reason } : {}
            );

            showApprovalToast(response.data.message || 'Request rejected', 'success');
            onActionComplete?.('reject');
            return true;
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to reject request';
            showApprovalToast(errorMessage, 'error');
            onActionError?.('reject', error instanceof Error ? error : new Error(errorMessage));
            return false;
          } finally {
            setLoading(false);
          }
        },
      },
    ];

    const modal = new Modal({
      title: 'Reject Request',
      content: contentDiv,
      variant: 'confirm',
      size: 'md',
      icon: 'xCircle',
      iconClass: 'text-destructive',
      actions,
    });

    modal.open();
  }

  // Create approve button
  const approveBtn = document.createElement('button');
  approveBtn.type = 'button';
  approveBtn.className = `
    inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium
    bg-success/20 text-success hover:bg-success/30
    transition-colors focus:outline-none focus:ring-2 focus:ring-success/50
    touch-btn-sm
  `.replace(/\s+/g, ' ').trim();
  approveBtn.title = 'Approve this request';
  approveBtn.appendChild(renderIcon('check', { size: 'xs', decorative: true }));
  const approveTxt = document.createElement('span');
  approveTxt.textContent = 'Approve';
  approveBtn.appendChild(approveTxt);
  approveBtn.addEventListener('click', handleApprove);

  // Create reject button
  const rejectBtn = document.createElement('button');
  rejectBtn.type = 'button';
  rejectBtn.className = `
    inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium
    bg-destructive/20 text-destructive hover:bg-destructive/30
    transition-colors focus:outline-none focus:ring-2 focus:ring-destructive/50
    touch-btn-sm
  `.replace(/\s+/g, ' ').trim();
  rejectBtn.title = 'Reject this request';
  rejectBtn.appendChild(renderIcon('x', { size: 'xs', decorative: true }));
  const rejectTxt = document.createElement('span');
  rejectTxt.textContent = 'Reject';
  rejectBtn.appendChild(rejectTxt);
  rejectBtn.addEventListener('click', handleReject);

  container.appendChild(approveBtn);
  container.appendChild(rejectBtn);

  return container;
}

/**
 * Toast notification for approval feedback
 */
export function showApprovalToast(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
  // Check if toast container exists, create if not
  let toastContainer = document.getElementById('approval-toast-container');
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'approval-toast-container';
    toastContainer.className = 'fixed bottom-4 right-4 z-[60] flex flex-col gap-2';
    document.body.appendChild(toastContainer);
  }

  // Create toast element
  const toast = document.createElement('div');
  const bgClass = type === 'success' ? 'bg-success' :
                  type === 'error' ? 'bg-destructive' : 'bg-info';
  const textClass = type === 'success' ? 'text-success-foreground' :
                    type === 'error' ? 'text-destructive-foreground' : 'text-info-foreground';

  toast.className = `
    flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg
    ${bgClass} ${textClass}
    transform translate-x-full opacity-0 transition-all duration-300
  `.replace(/\s+/g, ' ').trim();

  // Icon
  const iconName: IconName = type === 'success' ? 'checkCircle' :
                             type === 'error' ? 'xCircle' : 'info';
  toast.appendChild(renderIcon(iconName, { size: 'sm', decorative: true }));

  // Message
  const messageSpan = document.createElement('span');
  messageSpan.className = 'text-sm font-medium';
  messageSpan.textContent = message;
  toast.appendChild(messageSpan);

  // Close button
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'ml-2 p-1 rounded hover:bg-white/20 transition-colors touch-icon-btn';
  closeBtn.appendChild(renderIcon('x', { size: 'xs', decorative: true }));
  closeBtn.addEventListener('click', () => removeToast(toast));
  toast.appendChild(closeBtn);

  toastContainer.appendChild(toast);

  // Animate in
  requestAnimationFrame(() => {
    toast.classList.remove('translate-x-full', 'opacity-0');
    toast.classList.add('translate-x-0', 'opacity-100');
  });

  // Auto remove after 5 seconds
  setTimeout(() => removeToast(toast), 5000);
}

/**
 * Remove a toast element with animation
 */
function removeToast(toast: HTMLElement): void {
  toast.classList.remove('translate-x-0', 'opacity-100');
  toast.classList.add('translate-x-full', 'opacity-0');

  setTimeout(() => {
    toast.remove();
    // Clean up container if empty
    const container = document.getElementById('approval-toast-container');
    if (container && container.children.length === 0) {
      container.remove();
    }
  }, 300);
}
